import { getLocale } from "./language";

/** Backend numeric currency ids, in the order the API enumerates them. */
const CURRENCY_BY_INDEX = ["RUB", "UAH", "KZT", "BYN", "USD", "EUR"] as const;

const currencySymbols: Record<string, string> = {
  RUB: "₽",
  UAH: "₴",
  KZT: "₸",
  BYN: "Br",
  USD: "$",
  EUR: "€"
};

function currencyCode(currency: string | number) {
  return typeof currency === "number" ? (CURRENCY_BY_INDEX[currency] ?? String(currency)) : currency.toUpperCase();
}

export function getCurrencySymbol(currency: string | number) {
  const code = currencyCode(currency);
  return currencySymbols[code] ?? code;
}

/** Plain number in the selected language's notation (decimal and grouping separators). */
export function formatAmount(value: number, maximumFractionDigits = 2, locale = getLocale()) {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits,
    minimumFractionDigits: 0
  }).format(value);
}

/**
 * Monetary value in the account currency, laid out by the selected language (`1 234,5 €`, `€1,234.5`).
 * Codes Intl does not know (crypto tickers) keep the number and show the code after it.
 */
export function formatMoney(value: number, currency: string | number, locale = getLocale()) {
  const code = currencyCode(currency);
  if (/^[A-Z]{3}$/.test(code)) {
    try {
      const symbol = currencySymbols[code];
      const parts = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: code,
        currencyDisplay: symbol ? "narrowSymbol" : "code",
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      }).formatToParts(value);
      // Locale data disagrees on some symbols (BYN is "р."/"BYN", RUB is "руб." in be); keep the product's symbols.
      return parts.map((part) => (part.type === "currency" && symbol ? symbol : part.value)).join("");
    } catch {
      // Fall through to the plain layout below.
    }
  }
  return `${formatAmount(value, 2, locale)} ${getCurrencySymbol(code)}`;
}

export function formatCompactNumber(value: number, locale = getLocale()) {
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(value);
}

export function formatPercent(value: number, locale = getLocale()) {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 1
  }).format(value / 100);
}

function toDate(value: string | number | Date) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value: string | number | Date, locale = getLocale()) {
  const date = toDate(value);
  return date ? new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date) : "—";
}

export function formatDate(value: string | number | Date, options: Intl.DateTimeFormatOptions = { dateStyle: "medium" }, locale = getLocale()) {
  const date = toDate(value);
  return date ? new Intl.DateTimeFormat(locale, options).format(date) : "—";
}

export function formatAddress(value: string, visibleCharacters = 6) {
  if (value.length <= visibleCharacters * 2 + 1) {
    return value;
  }

  return `${value.slice(0, visibleCharacters)}…${value.slice(-visibleCharacters)}`;
}
